import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Pagination } from 'swiper';
import 'swiper/swiper-bundle.css';
import pageDotFilled from '../../assets/page-dot.svg';
import pageDotEmpty from '../../assets/page-dot-empty.svg';
import { Flex } from '../../../../../../wag-react/next-components/Flex';
import { Box } from '../../../../../../wag-react/next-components/box';
import { HomePageReview } from './components/HomePageReview';
import homepageWalkReviews from './reviews';

// Initialize Swiper modules
SwiperCore.use([Navigation, Pagination]);

// Create styled components for the carousel
const StyledSwiper = styled(Swiper)`
  width: 100%;
  padding-bottom: 10px;
  
  .swiper-wrapper {
    align-items: stretch;
    box-sizing: border-box;
    padding-bottom: 10px;
    padding-left: 4px;
  }
  
  .swiper-slide {
    height: auto;
    display: flex;
    justify-content: center;
    box-sizing: border-box;
  }
`;
const PaginationContainer = styled(Flex)`
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
`;
const DotsWrapper = styled(Flex)`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-x: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
  max-width: 100%;
  padding: 0 10px;
  
  @media (max-width: 768px) {
    justify-content: flex-start;
    width: 100%;
  }
`;
const PaginationDot = styled.img`
  width: 22px;
  height: 22px;
  margin: 0 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
`;
const NavigationButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #00BF8F;
  cursor: pointer;
  z-index: 5;
  padding: 0;
  margin: 0 10px;
  transition: color 0.3s ease;
  flex-shrink: 0;
  
  &:focus {
    outline: none;
  }
  
  &:disabled {
    color: #B2B2B2;
    cursor: not-allowed;
  }
`;

// Helper function to determine slides per view based on screen width
const getSlidesPerViewByWidth = (width: number): number => {
  if (width >= 1280) {
    return 4; // Large Desktop
  } else if (width >= 1024) {
    return 3; // Small Desktop
  } else if (width >= 768) {
    return 2; // Tablet
  }
  return 1; // Mobile
};
const HomePageReviewCarousel = () => {
  // Create ref for the swiper instance
  const swiperRef = useRef<SwiperCore | null>(null);
  const [slidesPerView, setSlidesPerView] = useState(1);
  const [activePage, setActivePage] = useState(0);
  // Add a ref to track manual navigation from pagination dots
  const isManualNavigation = useRef(false);
  // Add ref for the dots wrapper to scroll to active dot
  const dotsWrapperRef = useRef<HTMLDivElement>(null);

  // Add resize event listener to update active page when window size changes
  useEffect(() => {
    const handleResize = () => {
      if (swiperRef.current) {
        // Update the active page based on the current index and new slides per view
        setActivePage(getSwiperPaginationInfo().activePage);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Scroll to active dot when it changes
  useEffect(() => {
    // Use a more reliable way to get the dots wrapper element
    const dotsWrapper = document.querySelector('.dots-wrapper');
    if (dotsWrapper && window.innerWidth < 768) {
      // Force a small delay to ensure the DOM has updated
      setTimeout(() => {
        try {
          if (activePage < 6) {
            // Scroll to the beginning
            dotsWrapper.scrollLeft = 0;
          } else {
            // Scroll to the end
            const maxScroll = dotsWrapper.scrollWidth - dotsWrapper.clientWidth;
            dotsWrapper.scrollLeft = maxScroll;
          }
        } catch (error) {
          // todo: improve error handling
        }
      }, 100);
    }
  }, [activePage]);

  // Function to get pagination info from Swiper
  const getSwiperPaginationInfo = () => {
    if (!swiperRef.current) return {
      totalPages: 0,
      activePage: 0
    };
    const swiper = swiperRef.current;

    // Get the actual slides per view based on the current breakpoint
    const width = window.innerWidth;
    const actualSlidesPerView = getSlidesPerViewByWidth(width);
    const totalPages = Math.ceil(swiper.slides.length / actualSlidesPerView);
    const currentActivePage = Math.floor(swiper.activeIndex / actualSlidesPerView);
    return {
      totalPages,
      activePage: currentActivePage
    };
  };

  // Handle navigation button clicks
  const handlePrevClick = () => {
    if (swiperRef.current) {
      // Get the actual slides per view based on the current breakpoint
      const width = window.innerWidth;
      const actualSlidesPerView = getSlidesPerViewByWidth(width);

      // Set manual navigation flag to true for navigation buttons too
      isManualNavigation.current = true;

      // Calculate the new page index
      const prevPage = Math.max(0, activePage - 1);

      // Update activePage for immediate UI feedback
      setActivePage(prevPage);

      // Calculate the target slide index
      const targetIndex = prevPage * actualSlidesPerView;

      // Slide to the exact position
      swiperRef.current.slideTo(targetIndex);
    }
  };
  const handleNextClick = () => {
    if (swiperRef.current) {
      // Get the actual slides per view based on the current breakpoint
      const width = window.innerWidth;
      const actualSlidesPerView = getSlidesPerViewByWidth(width);

      // Get the total number of pages
      const {
        totalPages
      } = getSwiperPaginationInfo();

      // Set manual navigation flag to true for navigation buttons too
      isManualNavigation.current = true;

      // Calculate the new page index
      const nextPage = Math.min(totalPages - 1, activePage + 1);

      // Update activePage for immediate UI feedback
      setActivePage(nextPage);

      // Calculate the target slide index
      const targetIndex = nextPage * actualSlidesPerView;

      // Slide to the exact position
      swiperRef.current.slideTo(targetIndex);
    }
  };
  return <Flex width={1} boxSizing="border-box" flexDirection="column" alignItems="center" data-sentry-element="Flex" data-sentry-component="HomePageReviewCarousel" data-sentry-source-file="HomePageReviewCarousel.tsx">
      <Box width={1} pl={[2, 3, 4]} pr={[0, 3, 4]} data-sentry-element="Box" data-sentry-source-file="HomePageReviewCarousel.tsx">
        <Flex width={1} flexDirection="column" alignItems="center" data-sentry-element="Flex" data-sentry-source-file="HomePageReviewCarousel.tsx">
          <Box width="100%" maxWidth={['100%', '100%', 1100, 1150]} data-sentry-element="Box" data-sentry-source-file="HomePageReviewCarousel.tsx">
            <StyledSwiper slidesPerView={1.2} spaceBetween={16} pagination={{
            clickable: true,
            el: '.swiper-pagination',
            type: 'bullets'
          }}
          // We'll handle navigation through refs in onInit
          onInit={swiper => {
            // Store swiper instance in ref
            swiperRef.current = swiper;

            // Determine slides per view based on breakpoint
            const width = window.innerWidth;
            setSlidesPerView(getSlidesPerViewByWidth(width));

            // Listen for slide change events
            swiper.on('slideChange', () => {
              // Get the actual slides per view based on current width
              const currentWidth = window.innerWidth;
              const actualSlidesPerView = getSlidesPerViewByWidth(currentWidth);
              const newActiveIndex = swiper.activeIndex;
              const newActivePage = Math.floor(newActiveIndex / actualSlidesPerView);

              // Always update the active page to reflect the current position
              // This ensures the dots update even during manual sliding
              setActivePage(newActivePage);
            });

            // Also listen for transition end to ensure index is updated
            swiper.on('transitionEnd', () => {
              // Get the actual slides per view based on current width
              const currentWidth = window.innerWidth;
              const actualSlidesPerView = getSlidesPerViewByWidth(currentWidth);
              const newActiveIndex = swiper.activeIndex;
              const newActivePage = Math.floor(newActiveIndex / actualSlidesPerView);

              // If it was a manual navigation, reset the flag
              if (isManualNavigation.current) {
                isManualNavigation.current = false;
              }

              // Always update the active page to reflect the current position
              setActivePage(newActivePage);
            });
          }} onResize={() => {
            // Update slides per view on resize
            const width = window.innerWidth;
            const newSlidesPerView = getSlidesPerViewByWidth(width);
            setSlidesPerView(newSlidesPerView);

            // Also update active page on resize to ensure pagination is correct
            if (swiperRef.current) {
              const newActiveIndex = swiperRef.current.activeIndex;
              const newActivePage = Math.floor(newActiveIndex / newSlidesPerView);
              setActivePage(newActivePage);
            }
          }} breakpoints={{
            768: {
              slidesPerView: 2,
              spaceBetween: 24,
              slidesPerGroup: 2
            },
            1024: {
              slidesPerView: 3,
              spaceBetween: 24,
              slidesPerGroup: 3
            },
            1280: {
              slidesPerView: 4,
              spaceBetween: 20,
              slidesPerGroup: 4
            }
          }} data-sentry-element="StyledSwiper" data-sentry-source-file="HomePageReviewCarousel.tsx">
              {homepageWalkReviews.map(review => <SwiperSlide key={`review-${review.name}-${review.gender}`} style={{
              width: 'auto',
              height: 'auto'
            }}>
                  <Box width={['100%', '300px', '317px', '270px']} height="420px" mx="auto" boxSizing="border-box">
                    <HomePageReview {...review} />
                  </Box>
                </SwiperSlide>)}
            </StyledSwiper>
            
            <PaginationContainer width="100%" justifyContent="center" alignItems="center" data-sentry-element="PaginationContainer" data-sentry-source-file="HomePageReviewCarousel.tsx">
              <NavigationButton onClick={handlePrevClick} disabled={activePage === 0} data-sentry-element="NavigationButton" data-sentry-source-file="HomePageReviewCarousel.tsx">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-sentry-element="svg" data-sentry-source-file="HomePageReviewCarousel.tsx">
                  <path d="M14.7071 5.29289C15.0976 5.68342 15.0976 6.31658 14.7071 6.70711L10.4142 11L14.7071 15.2929C15.0976 15.6834 15.0976 16.3166 14.7071 16.7071C14.3166 17.0976 13.6834 17.0976 13.2929 16.7071L8.29289 11.7071C7.90237 11.3166 7.90237 10.6834 8.29289 10.2929L13.2929 5.29289C13.6834 4.90237 14.3166 4.90237 14.7071 5.29289Z" fill="currentColor" data-sentry-element="path" data-sentry-source-file="HomePageReviewCarousel.tsx" />
                </svg>
              </NavigationButton>
              <DotsWrapper ref={dotsWrapperRef} className="dots-wrapper" data-sentry-element="DotsWrapper" data-sentry-source-file="HomePageReviewCarousel.tsx">
                {Array.from({
                length: getSwiperPaginationInfo().totalPages
              }).map((_, pageNumber) => {
                const isActive = activePage === pageNumber;
                const dotId = `pagination-${homepageWalkReviews[pageNumber * slidesPerView]?.name ?? pageNumber}-${pageNumber}`;
                return <PaginationDot key={dotId} src={isActive ? pageDotFilled : pageDotEmpty} onClick={() => {
                  if (swiperRef.current) {
                    // Get the actual slides per view based on the current breakpoint
                    const width = window.innerWidth;
                    const actualSlidesPerView = getSlidesPerViewByWidth(width);

                    // Set manual navigation flag to true
                    isManualNavigation.current = true;

                    // Update both states for immediate UI feedback
                    setActivePage(pageNumber);

                    // Calculate the target slide index based on the actual slides per view
                    const targetIndex = pageNumber * actualSlidesPerView;

                    // Then slide to the position
                    swiperRef.current.slideTo(targetIndex);
                  }
                }} alt={`Page ${pageNumber + 1}`} />;
              })}
              </DotsWrapper>
              <NavigationButton onClick={handleNextClick} disabled={activePage >= getSwiperPaginationInfo().totalPages - 1} data-sentry-element="NavigationButton" data-sentry-source-file="HomePageReviewCarousel.tsx">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-sentry-element="svg" data-sentry-source-file="HomePageReviewCarousel.tsx">
                  <path d="M9.29289 5.29289C8.90237 5.68342 8.90237 6.31658 9.29289 6.70711L13.5858 11L9.29289 15.2929C8.90237 15.6834 8.90237 16.3166 9.29289 16.7071C9.68342 17.0976 10.3166 17.0976 10.7071 16.7071L15.7071 11.7071C16.0976 11.3166 16.0976 10.6834 15.7071 10.2929L10.7071 5.29289C10.3166 4.90237 9.68342 4.90237 9.29289 5.29289Z" fill="currentColor" data-sentry-element="path" data-sentry-source-file="HomePageReviewCarousel.tsx" />
                </svg>
              </NavigationButton>
            </PaginationContainer>
          </Box>
        </Flex>
      </Box>
    </Flex>;
};
export default HomePageReviewCarousel;